// GeexValueRules — Geex frame 09 (workspace/design-refs/geex/
// 08-analytics-grid-bars-orange.png, "Saved & Invested" — the paired frame
// ships in the same source PNG as GeexGridBars/08 on the geex share grid):
// sparse vertical ink ticks at data points (thin marks + a value label, no
// axis/gridlines — height alone encodes magnitude) plus one pattern-filled
// proportional footer bar for the category breakdown.
//
// items: [{ label, value }] — the sparse tick series; value is the raw
// number driving tick height (max-normalized), evenly spread left-to-right.
// segments: [{ label, value, texture? }] — footer proportional bar; value
// is the percent share, texture is 'solid' | 'hatched' | 'faint' (defaults
// to that exact 3-step cycle by index — the reference frame's device).
// ink / hue: same contract as the other primitives — hue tints the
// 'hatched'/'faint' footer segments when set, else ink-alpha.
// height: tick-plot height — a spacing-scale token (default 'E', ~117px
// at base scale).

// FA204 / frank trap — helper lives in `scope`, not module scope (a bare
// module-scope const reads as undefined post-frank.toJSON and kills the
// footer-segment factories).
export const GeexValueRules = {
  scope: {
    segTone: (ink, hue, texture) => {
      const base = hue || ink
      if (texture === 'hatched') return { color: `${base}.55`, image: true }
      if (texture === 'faint') return { color: `${base}.12`, image: false }
      return { color: ink, image: false }
    }
  },
  flow: 'y',
  gap: 'C',
  // The Plot's children are absolutely positioned, so nothing gives this
  // primitive intrinsic width — without an explicit width the root
  // collapses when a consumer centers it and every tick lands at x≈0.
  width: '100%',
  items: [],
  segments: [],
  ink: 'title',
  height: 'E',

  Plot: {
    position: 'relative',
    height: (el) => el.parent.height || 'E',
    childrenAs: 'state',
    children: (el, s) => {
      const parent = el.parent
      const items = parent.items || []
      const n = items.length || 1
      const max = items.reduce((m, it) => Math.max(m, Number(it.value) || 0), 0) || 1
      const ink = parent.ink || 'title'
      return items.map((it, i) => ({
        key: it.label || `tick${i}`,
        left: n > 1 ? (i / (n - 1)) * 82 : 0,
        pct: Math.max((Number(it.value) || 0) / max, 0.08) * 100,
        label: it.label || '',
        ink
      }))
    },
    childExtends: {
      position: 'absolute',
      left: (el, s) => `${s.left}%`,
      bottom: (el, s) => `${s.pct}%`,
      flow: 'x',
      align: 'center start',
      gap: 'X',

      Tick: { width: 'V', height: 'C', background: (el, s) => s.ink, flexShrink: '0' },
      Label: {
        tag: 'span',
        fontSize: 'Z',
        fontFamily: 'Code',
        fontWeight: '700',
        whiteSpace: 'nowrap',
        color: (el, s) => s.ink,
        // `items[].label` is a KEY-IN-STATE carrier: a consumer may hand this
        // chart a bundle key (workspace/pages/admin/usage.js feeds
        // `admin.usage.attr.systemEmpty`). Resolved HERE, at the render site —
        // `translate()` returns its input unchanged when nothing resolves, so a
        // tenant-supplied label passes through untouched.
        text: (el, s) => el.call('polyglot', s.label || '')
      }
    }
  },

  Footer: {
    flow: 'y',
    gap: 'X',
    hide: (el) => !(el.parent.segments || []).length,

    Bar: {
      flow: 'x',
      width: '100%',
      height: 'C',
      round: 'X',
      overflow: 'hidden',
      childrenAs: 'state',
      children: (el, s) => {
        const parent = el.parent.parent
        const ink = parent.ink || 'title'
        const hue = parent.hue
        const cycle = ['solid', 'hatched', 'faint']
        return (parent.segments || []).map((sg, i) => ({
          key: sg.label || `seg${i}`,
          flexPct: Math.max(Number(sg.value) || 0, 1),
          texture: sg.texture || cycle[i % cycle.length],
          ink,
          hue
        }))
      },
      childExtends: {
        flex: (el, s) => String(s.flexPct),
        height: '100%',
        color: (el, s) => el.scope.segTone(s.ink, s.hue, s.texture).color,
        background: (el, s) =>
          el.scope.segTone(s.ink, s.hue, s.texture).image ? 'transparent' : 'currentColor',
        backgroundImage: (el, s) =>
          el.scope.segTone(s.ink, s.hue, s.texture).image
            ? 'repeating-linear-gradient(135deg, currentColor 0, currentColor 3px, transparent 3px, transparent 7px)'
            : 'none'
      }
    },

    Captions: {
      flow: 'x',
      childrenAs: 'state',
      children: (el, s) => {
        const parent = el.parent.parent
        const ink = parent.ink || 'title'
        return (parent.segments || []).map((sg, i) => ({
          key: sg.label || `cap${i}`,
          flexPct: Math.max(Number(sg.value) || 0, 1),
          value: sg.value,
          label: sg.label || '',
          ink
        }))
      },
      childExtends: {
        flow: 'y',
        gap: 'V',
        flex: (el, s) => String(s.flexPct),
        Value: {
          tag: 'span',
          fontSize: 'B',
          fontWeight: '700',
          fontFamily: 'Code',
          color: (el, s) => s.ink,
          text: (el, s) => `${s.value ?? 0}%`
        },
        Label: {
          tag: 'span',
          fontSize: 'Z',
          opacity: '0.45',
          color: (el, s) => s.ink,
          text: (el, s) => el.call('polyglot', s.label || '')
        }
      }
    }
  }
}
